// Standardised Alvora Excel import templates: column definitions + download.
import * as XLSX from 'xlsx';

export const TENDER_COMPONENTS = [
  { key: 'measured_works', label: 'Measured Works', match: /measured|bill|boq|works/ },
  { key: 'preliminaries', label: 'Preliminaries', match: /prelim/ },
  { key: 'provisional_sums', label: 'Provisional Sums', match: /provisional|\bps\b|prime\s*cost|\bpc\b/ },
  { key: 'contingencies', label: 'Contingencies', match: /contingen/ },
  { key: 'adjustments', label: 'Adjustments', match: /adjust|discount|omission|addition|allowance/ },
  { key: 'vat', label: 'VAT', match: /\bvat\b|value added tax/ },
  { key: 'tender_total', label: 'Tender Total', match: /tender\s*total|grand\s*total|contract\s*(sum|total)|total\s*(inc|incl)/ },
];

export const IMPORT_TYPES = {
  priced_boq: {
    key: 'priced_boq',
    label: 'Priced BOQ',
    short: 'BOQ',
    fileStem: 'Alvora_Priced_BOQ_Template',
    description: 'Client-facing bill of quantities with selling rates. Reconciles to the confirmed Tender Summary (ex-VAT) or a manually entered expected total.',
    visibility: 'All organisation members',
    columns: [
      { key: 'trade', header: 'Trade', required: true, type: 'text', aliases: ['trade section', 'section'] },
      { key: 'cost_code', header: 'Cost Code', required: true, type: 'text', aliases: ['code', 'costcode'] },
      { key: 'item_ref', header: 'Item Ref', required: false, type: 'text', aliases: ['ref', 'item', 'item reference', 'reference'] },
      { key: 'description', header: 'Description', required: true, type: 'text', aliases: ['desc'] },
      { key: 'unit', header: 'Unit', required: false, type: 'text', aliases: ['uom'] },
      { key: 'quantity', header: 'Quantity', required: false, type: 'number', aliases: ['qty'] },
      { key: 'selling_rate', header: 'Selling Rate', required: false, type: 'number', aliases: ['rate', 'sell rate'] },
      { key: 'selling_total', header: 'Selling Total', required: true, type: 'number', aliases: ['total', 'amount', 'sell total'] },
    ],
    totalKey: 'selling_total',
    widths: [18, 12, 10, 48, 8, 12, 14, 16],
  },
  tender_summary: {
    key: 'tender_summary',
    label: 'Tender Summary',
    short: 'Tender',
    fileStem: 'Alvora_Tender_Summary_Template',
    description: 'Measured works, preliminaries, provisional sums, contingencies, adjustments, VAT and the tender total. Exactly one Tender Total line.',
    visibility: 'All organisation members',
    columns: [
      { key: 'component', header: 'Component', required: true, type: 'component', aliases: ['item', 'line', 'element'] },
      { key: 'description', header: 'Description', required: false, type: 'text', aliases: ['desc', 'notes'] },
      { key: 'amount', header: 'Amount', required: true, type: 'number', aliases: ['total', 'value', 'eur', '€'] },
    ],
    totalKey: 'amount',
    widths: [22, 48, 16],
    seedRows: TENDER_COMPONENTS.map((c) => ({ component: c.label, description: '', amount: '' })),
  },
  cost_budget: {
    key: 'cost_budget',
    label: 'Internal Cost Budget',
    short: 'Cost Budget',
    fileStem: 'Alvora_Internal_Cost_Budget_Template',
    description: 'Estimated direct costs per cost code: labour, materials, subcontractor, plant and other. Confidential — owner, admin and manager only.',
    visibility: 'Owner, Admin, Manager',
    restricted: true,
    columns: [
      { key: 'trade', header: 'Trade', required: true, type: 'text', aliases: ['trade section', 'section'] },
      { key: 'cost_code', header: 'Cost Code', required: true, type: 'text', aliases: ['code', 'costcode'] },
      { key: 'item_ref', header: 'Item Ref', required: false, type: 'text', aliases: ['ref', 'item', 'reference'] },
      { key: 'description', header: 'Description', required: false, type: 'text', aliases: ['desc'] },
      { key: 'labour', header: 'Labour', required: false, type: 'number', aliases: ['labor'] },
      { key: 'materials', header: 'Materials', required: false, type: 'number', aliases: ['material'] },
      { key: 'subcontractor', header: 'Subcontractor', required: false, type: 'number', aliases: ['subcontract', 'sub-contractor', 'subbie'] },
      { key: 'plant', header: 'Plant', required: false, type: 'number', aliases: ['plant & equipment', 'equipment'] },
      { key: 'other', header: 'Other', required: false, type: 'number', aliases: ['other direct costs', 'other costs', 'misc'] },
      { key: 'total_cost', header: 'Total Cost', required: false, type: 'number', aliases: ['total', 'cost total'] },
    ],
    totalKey: 'total_cost',
    widths: [18, 12, 10, 44, 12, 12, 14, 10, 10, 14],
  },
};

export const IMPORT_TYPE_ORDER = ['tender_summary', 'priced_boq', 'cost_budget'];

export function downloadTemplate(typeKey) {
  const t = IMPORT_TYPES[typeKey];
  const headers = t.columns.map((c) => c.header);
  const rows = (t.seedRows || []).map((r) => t.columns.map((c) => r[c.key] ?? ''));
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws['!cols'] = t.widths.map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, t.label.slice(0, 31));
  XLSX.writeFile(wb, `${t.fileStem}.xlsx`);
}
